import { useState, type FormEvent } from 'react';
import {
  BellRing,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  Eye,
  ExternalLink,
  FileText,
  MailCheck,
  MailOpen,
  MousePointerClick,
  PauseCircle,
  Pencil,
  Plus,
  Send,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { EmptyState } from '@/components/empty-state';
import { FileDrop } from '@/components/file-drop';
import { IconButton } from '@/components/icon-button';
import { Pager } from '@/components/pager';
import { EmailComposerDialog, type ComposedEmail } from '@/components/email-composer';
import { summarizeRecipients } from '@/lib/mail-recipients';
import { formatDateLong, formatDateTime, formatMoney, formatStatus } from '@/lib/format';
import {
  approvableDeleteWarning,
  useApprovables,
  useDeferApprovable,
  useDeleteApprovable,
  useRemindApprovable,
  useSendApprovable,
  useUpdateApprovable,
  useUploadApprovable,
  type ApprovableUploadInput,
} from '@/lib/approvables';
import { useToast } from '@/components/toast';
import { useConfirm } from '@/components/confirm';
import { useMe } from '@/lib/auth';
import { FileViewer } from '@/components/file-viewer';
import { useFormError } from '@/lib/form-error';
import type { ApprovableData } from '@/types/api';

const TYPE_LABEL: Record<string, string> = {
  proposal: 'Proposal',
  hardware_auth: 'Hardware auth',
};

/** Past tense: an approval row records something that already happened. */
const ACTION_LABEL: Record<string, string> = {
  approve: 'Approved',
  decline: 'Declined',
  defer: 'Put on hold',
};

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  sent: 'info',
  approved: 'success',
  paid: 'success',
  declined: 'danger',
  expired: 'danger',
  deferred: 'warning',
};

export function ClientApprovables({ clientId }: { clientId: number }) {
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = useApprovables(clientId, page);
  const data = paginated?.data;
  const meta = paginated?.meta;
  const remove = useDeleteApprovable(clientId);
  const confirm = useConfirm();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  /**
   * Deletable at any status, signed and paid included. The dialog is the only
   * thing standing between a click and a gone record, so it always runs.
   */
  async function deleteApprovable(approvable: ApprovableData) {
    const ok = await confirm({
      title: `Delete ${approvable.title}?`,
      body: approvableDeleteWarning(approvable),
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await remove.mutateAsync(approvable.id);
      toast.success(`${approvable.title} deleted`);
    } catch (err) {
      toast.error('Something went wrong', err instanceof Error ? err.message : undefined);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" /> New approvable
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No approvables yet"
          description="Upload a proposal or hardware authorization PDF and send it to the client for signature."
        />
      ) : (
        <>
          <div className="space-y-3">
            {data.map((a) => (
              <ApprovableRow
                key={a.id}
                approvable={a}
                clientId={clientId}
                onDelete={() => void deleteApprovable(a)}
              />
            ))}
          </div>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </>
      )}

      <UploadDialog
        clientId={clientId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}

function ApprovableRow({
  approvable,
  clientId,
  onDelete,
}: {
  approvable: ApprovableData;
  clientId: number;
  onDelete: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);
  const defer = useDeferApprovable(clientId);
  const isDraft = approvable.status === 'draft';
  const canRemind =
    approvable.status === 'sent' || approvable.status === 'deferred';
  const canDefer = approvable.status === 'sent' && approvable.type === 'proposal';

  async function copyLink() {
    if (!approvable.public_url) return;
    await navigator.clipboard.writeText(approvable.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Card>
      <CardHeader className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{approvable.title}</span>
            <Badge tone="neutral">{TYPE_LABEL[approvable.type] ?? approvable.type}</Badge>
            <Badge tone={STATUS_TONE[approvable.status] ?? 'neutral'}>
              <StatusIcon status={approvable.status} />
              <span className="ml-1">{formatStatus(approvable.status)}</span>
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[var(--color-foreground-muted)]">
            {approvable.total_cents ? (
              <span>{formatMoney(approvable.total_cents, approvable.currency)}</span>
            ) : null}
            {approvable.deposit_cents ? (
              <span>deposit {formatMoney(approvable.deposit_cents, approvable.currency)}</span>
            ) : null}
            <span>· {approvable.recipient_email}</span>
            {approvable.sent_at ? (
              <span>· sent {formatDateLong(approvable.sent_at)}</span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isDraft || approvable.status === 'deferred' ? (
            <Button size="sm" variant="secondary" onClick={() => setSending(true)}>
              <Send className="size-3.5" /> Send
            </Button>
          ) : null}
          {canDefer ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => defer.mutate(approvable.id)}
              disabled={defer.isPending}
            >
              <PauseCircle className="size-3.5" /> Put on hold
            </Button>
          ) : null}
          {canRemind ? (
            <Button size="sm" variant="secondary" onClick={() => setReminding(true)}>
              <BellRing className="size-3.5" /> Remind
              {approvable.reminder_count > 0 ? (
                <span className="ml-1 rounded bg-foreground/10 px-1 text-[10px] tabular-nums">
                  {approvable.reminder_count}
                </span>
              ) : null}
            </Button>
          ) : null}
          {approvable.receipt_url ? (
            <IconButton
              onClick={() => window.open(approvable.receipt_url!, '_blank')}
              aria-label="Download receipt PDF"
            >
              <Download className="size-4 text-[var(--color-success)]" />
            </IconButton>
          ) : null}
          <IconButton onClick={() => setPreviewing(true)} aria-label="Preview PDF">
            <Eye className="size-4" />
          </IconButton>
          {isDraft ? (
            <IconButton onClick={() => setEditing(true)} aria-label="Edit">
              <Pencil className="size-4" />
            </IconButton>
          ) : null}
          <IconButton onClick={copyLink} aria-label="Copy public link">
            {copied ? <CheckCircle2 className="size-4 text-[var(--color-success)]" /> : <Copy className="size-4" />}
          </IconButton>
          <IconButton
            onClick={() => approvable.public_url && window.open(approvable.public_url, '_blank')}
            aria-label="Open public link"
          >
            <ExternalLink className="size-4" />
          </IconButton>
          <IconButton onClick={onDelete} aria-label="Delete">
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      </CardHeader>

      <Dialog
        open={previewing}
        onClose={() => setPreviewing(false)}
        title={approvable.title}
        size="lg"
      >
        {approvable.file_url ? (
          <div className="h-[70vh] w-full overflow-auto rounded border border-[var(--color-border)] bg-[var(--color-surface-muted)]">
            <FileViewer url={approvable.file_url} mimeType="application/pdf" fileName={approvable.title} />
          </div>
        ) : null}
      </Dialog>

      {editing ? (
        <EditApprovableDialog
          approvable={approvable}
          clientId={clientId}
          onClose={() => setEditing(false)}
        />
      ) : null}
      {sending ? (
        <SendApprovableDialog
          approvable={approvable}
          clientId={clientId}
          onClose={() => setSending(false)}
        />
      ) : null}
      {reminding ? (
        <SendApprovableDialog
          approvable={approvable}
          clientId={clientId}
          onClose={() => setReminding(false)}
          mode="remind"
        />
      ) : null}
      {approvable.email_engagement ? (
        <CardBody className="flex flex-wrap items-center gap-2 pt-0 text-xs text-[var(--color-foreground-muted)]">
          <EngagementChip
            kind="delivered"
            at={approvable.email_engagement.delivered_at as string | null}
          />
          <EngagementChip
            kind="opened"
            at={approvable.email_engagement.opened_at as string | null}
            count={(approvable.email_engagement.open_count as number) ?? 0}
          />
          <EngagementChip
            kind="clicked"
            at={approvable.email_engagement.clicked_at as string | null}
            count={(approvable.email_engagement.click_count as number) ?? 0}
          />
          {approvable.last_reminded_at ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-[var(--color-surface-muted)] px-2 py-0.5">
              <BellRing className="size-3" /> Reminded {formatDateTime(approvable.last_reminded_at)}
            </span>
          ) : null}
        </CardBody>
      ) : null}
      {approvable.approvals.length > 0 ? (
        <CardBody className="space-y-1.5 text-xs text-[var(--color-foreground-muted)]">
          {approvable.approvals.map((ap) => (
            <div key={ap.id} className="flex flex-wrap items-center gap-2">
              <Badge tone={ap.action === 'approve' ? 'success' : ap.action === 'decline' ? 'danger' : 'warning'}>
                {ACTION_LABEL[ap.action] ?? formatStatus(ap.action)}
              </Badge>
              {ap.actor_name ? (
                <span>
                  by {ap.actor_name}
                  {ap.contact_id ? null : ' (not on file)'}
                </span>
              ) : null}
              <span>· {formatDateTime(ap.created_at)}</span>
              {ap.verified_at ? (
                <span
                  className="inline-flex items-center gap-1 text-[var(--color-success)]"
                  title={`Identity confirmed by a code texted to the number ending ${ap.verified_phone_last4} on file`}
                >
                  <ShieldCheck className="size-3" /> Verified by text
                </span>
              ) : null}
              {ap.comment ? <span className="italic">— {ap.comment}</span> : null}
            </div>
          ))}
        </CardBody>
      ) : null}
    </Card>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'approved' || status === 'paid') return <CheckCircle2 className="size-3" />;
  if (status === 'declined' || status === 'expired') return <XCircle className="size-3" />;
  return <Clock className="size-3" />;
}

function UploadDialog({
  clientId,
  open,
  onClose,
}: {
  clientId: number;
  open: boolean;
  onClose: () => void;
}) {
  const upload = useUploadApprovable(clientId);
  const [file, setFile] = useState<File | null>(null);
  const [type, setType] = useState<ApprovableUploadInput['type']>('proposal');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [total, setTotal] = useState('');
  const [deposit, setDeposit] = useState('');
  const { error, wrap, clear } = useFormError();
  const [fileError, setFileError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!file) {
      setFileError('Upload a PDF.');
      return;
    }
    setFileError(null);
    clear();
    const ok = await wrap(() =>
      upload.mutateAsync({
        type,
        title: title || file.name,
        description: description || undefined,
        total_cents: total ? Math.round(parseFloat(total) * 100) : undefined,
        deposit_cents: deposit ? Math.round(parseFloat(deposit) * 100) : undefined,
        file,
      }),
    );
    if (ok) {
      onClose();
      setFile(null);
      setTitle('');
      setDescription('');
      setTotal('');
      setDeposit('');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="New approvable" size="lg">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <FileDrop file={file} onChange={setFile} accept="application/pdf" />
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1 text-xs">
            Type
            <Select value={type} onChange={(e) => setType(e.target.value as ApprovableUploadInput['type'])}>
              <option value="proposal">Proposal</option>
              <option value="hardware_auth">Hardware authorization</option>
            </Select>
          </label>
          <label className="space-y-1 text-xs">
            Title
            <Input
              placeholder={file?.name ?? ''}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            Total ($)
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            Deposit ($)
            <Input
              inputMode="decimal"
              placeholder="optional"
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
            />
          </label>
          <label className="col-span-2 space-y-1 text-xs">
            Description / notes for the recipient (optional)
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </div>
        {fileError ? <p className="text-sm text-[var(--color-danger)]">{fileError}</p> : null}
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-muted-foreground">
            You'll pick who this goes to when you send it.
          </p>
          <Button type="submit" disabled={upload.isPending}>
            {upload.isPending ? 'Creating…' : 'Create draft'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function SendApprovableDialog({
  approvable,
  clientId,
  onClose,
  mode = 'send',
}: {
  approvable: ApprovableData;
  clientId: number;
  onClose: () => void;
  mode?: 'send' | 'remind';
}) {
  const send = useSendApprovable(clientId);
  const remind = useRemindApprovable(clientId);
  const action = mode === 'remind' ? remind : send;
  const toast = useToast();
  const me = useMe();
  const typeLabel = TYPE_LABEL[approvable.type] ?? approvable.type;
  const senderName = me.data?.name ?? 'Mehrutherm';

  async function handleSend(email: ComposedEmail) {
    await action.mutateAsync({ approvableId: approvable.id, ...email });
    const who = summarizeRecipients(email);
    toast.success(mode === 'remind' ? `Reminder sent to ${who}` : `Sent to ${who}`);
    onClose();
  }

  return (
    <EmailComposerDialog
      source={{
        kind: 'record',
        type: mode === 'remind' ? 'approvable.reminder' : 'approvable.invitation',
        id: approvable.id,
      }}
      title={mode === 'remind' ? `Remind about this ${typeLabel.toLowerCase()}` : `Send ${typeLabel.toLowerCase()}`}
      confirmLabel={mode === 'remind' ? 'Send reminder' : 'Send now'}
      pending={action.isPending}
      onClose={onClose}
      onSend={handleSend}
      lockedNote={`The total, the review button and its private link, and your sign-off (${senderName}) are added below your message.`}
      summary={
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
          <div className="truncate font-medium">{approvable.title}</div>
          <div className="text-xs text-muted-foreground">
            {typeLabel}
            {approvable.total_cents ? (
              <> · {formatMoney(approvable.total_cents, approvable.currency)}</>
            ) : null}
            {approvable.deposit_cents ? (
              <> (deposit {formatMoney(approvable.deposit_cents, approvable.currency)})</>
            ) : null}
          </div>
        </div>
      }
    />
  );
}

function EngagementChip({
  kind,
  at,
  count,
}: {
  kind: 'delivered' | 'opened' | 'clicked';
  at: string | null;
  count?: number;
}) {
  if (!at) return null;
  const Icon = kind === 'delivered' ? MailCheck : kind === 'opened' ? MailOpen : MousePointerClick;
  const label = kind === 'delivered' ? 'Delivered' : kind === 'opened' ? 'Opened' : 'Clicked';
  const tone =
    kind === 'delivered'
      ? 'bg-[var(--color-surface-muted)]'
      : kind === 'opened'
        ? 'bg-blue-500/10 text-blue-700 dark:text-blue-300'
        : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${tone}`} title={at}>
      <Icon className="size-3" />
      <span>
        {label} {formatDateTime(at)}
        {count && count > 1 ? <span className="ml-1 opacity-75">·×{count}</span> : null}
      </span>
    </span>
  );
}

function EditApprovableDialog({
  approvable,
  clientId,
  onClose,
}: {
  approvable: ApprovableData;
  clientId: number;
  onClose: () => void;
}) {
  const update = useUpdateApprovable(clientId);
  const { error, wrap } = useFormError();
  const [title, setTitle] = useState(approvable.title);
  const [description, setDescription] = useState(approvable.description ?? '');
  const [total, setTotal] = useState(
    approvable.total_cents ? (approvable.total_cents / 100).toString() : '',
  );
  const [deposit, setDeposit] = useState(
    approvable.deposit_cents ? (approvable.deposit_cents / 100).toString() : '',
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      update.mutateAsync({
        id: approvable.id,
        patch: {
          title,
          description: description || undefined,
          total_cents: total ? Math.round(parseFloat(total) * 100) : null,
          deposit_cents: deposit ? Math.round(parseFloat(deposit) * 100) : null,
        },
      }),
    );
    if (ok) onClose();
  }

  return (
    <Dialog open={true} onClose={onClose} title={`Edit ${approvable.title}`} size="lg">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 space-y-1 text-xs">
            Title
            <Input required value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="space-y-1 text-xs">
            Total ({approvable.currency})
            <Input
              type="number"
              step="0.01"
              min="0"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
          </label>
          <label className="space-y-1 text-xs">
            Deposit
            <Input
              type="number"
              step="0.01"
              min="0"
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
            />
          </label>
          <label className="col-span-2 space-y-1 text-xs">
            Description (optional)
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </div>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
